const { PAYMENT_STATUS, PAYMENT_CREDIT_CARD_ERROR_CODES } = require('../../../general/constants');

const PAYMENT_INFO_TYPE = {
    CREDIT_CARD: 'credit_card',
    CREDIT_CARD_AND_POINTS: 'credit_card_and_points',
    BNPL: 'bnpl',
    POINTS: 'points',
};

const PAYMENT_DECLINE_TYPE = {
    CARD_DECLINED: 'card_declined',
    CARD_EXPIRED: 'card_expired',
    CARD_NOT_SUPPORTED: 'card_not_supported',
    INSUFFICIENT_POINTS: 'insufficient_points',
    POINTS_CHARGE_FAILED: 'points_charge_failed',
    PAYMENT_FAILED: 'payment_failed',
};

// postMessage type sent by the gateway error page to the checkout / payment-link page that hosts it.
const PAYMENT_DECLINED_MESSAGE_TYPE = 'PAYMENT_DECLINED';

// The card passed the gateway once the payment leaves INITIALIZED for one of these (J5 hold, billing, BNPL token).
const PAYMENT_METHOD_ACCEPTED_STATUSES = [
    PAYMENT_STATUS.J5,
    PAYMENT_STATUS.BILLED_IN_PROCESS,
    PAYMENT_STATUS.BILLED,
    PAYMENT_STATUS.BNPL_PENDING,
    PAYMENT_STATUS.CHARGE_LATER,
];

// CreditGuard expired-card result codes (mirrors EXPIRED_CARD_CG_CODES in @arbitrip/payments).
const CREDITGUARD_EXPIRED_CARD_CODES = ['033', '036', '054'];

const DECLINE_TYPE_BY_PAYMENT_ERROR_CODE = {
    [PAYMENT_CREDIT_CARD_ERROR_CODES.CREDIT_CARD_NOT_ACCEPTED_ERROR]: PAYMENT_DECLINE_TYPE.CARD_DECLINED,
    [PAYMENT_CREDIT_CARD_ERROR_CODES.CREDIT_CARD_NOT_SUPPORTED_LOYALTY_CARD]: PAYMENT_DECLINE_TYPE.CARD_NOT_SUPPORTED,
    [PAYMENT_CREDIT_CARD_ERROR_CODES.INSUFFICIENT_POINTS_ERROR]: PAYMENT_DECLINE_TYPE.INSUFFICIENT_POINTS,
    [PAYMENT_CREDIT_CARD_ERROR_CODES.CHARGE_LOYALTY_POINTS_FAILED]: PAYMENT_DECLINE_TYPE.POINTS_CHARGE_FAILED,
};

const CARD_BRAND = {
    VISA: 'visa',
    MASTERCARD: 'mastercard',
    AMEX: 'amex',
    DINERS: 'diners',
    ISRACARD: 'isracard',
    MAESTRO: 'maestro',
    JCB: 'jcb',
    DISCOVER: 'discover',
};

// Keys are gateway brand names lowercased with spaces, dashes and underscores removed.
const CARD_BRAND_BY_GATEWAY_NAME = {
    visa: CARD_BRAND.VISA,
    mastercard: CARD_BRAND.MASTERCARD,
    amex: CARD_BRAND.AMEX,
    americanexpress: CARD_BRAND.AMEX,
    diners: CARD_BRAND.DINERS,
    dinersclub: CARD_BRAND.DINERS,
    isracard: CARD_BRAND.ISRACARD,
    maestro: CARD_BRAND.MAESTRO,
    jcb: CARD_BRAND.JCB,
    discover: CARD_BRAND.DISCOVER,
};

const RE_BRAND_SEPARATORS = /[\s_-]/g;
const MAX_DECLINE_TEXT_LENGTH = 100;
// Gateway texts may echo card numbers, phones or emails (also spaced/dashed); those must never reach analytics.
const RE_SENSITIVE_TEXT = /@|\d{5,}/;
const RE_DIGIT_SEPARATORS = /[\s().\/-]/g;
const RE_GATEWAY_CODE = /^\d{1,4}$/;

function normalizeCardBrand(rawBrand) {
    if (typeof rawBrand !== 'string') return null;
    return CARD_BRAND_BY_GATEWAY_NAME[rawBrand.trim().toLowerCase().replace(RE_BRAND_SEPARATORS, '')] || null;
}

function getPaymentInfoType({ isBnpl = false, isPointsOnly = false, points = 0 } = {}) {
    if (isPointsOnly) return PAYMENT_INFO_TYPE.POINTS;
    if (isBnpl) return PAYMENT_INFO_TYPE.BNPL;
    return Number(points) > 0 ? PAYMENT_INFO_TYPE.CREDIT_CARD_AND_POINTS : PAYMENT_INFO_TYPE.CREDIT_CARD;
}

function isPaymentMethodAcceptedStatus(status) {
    return PAYMENT_METHOD_ACCEPTED_STATUSES.includes(status);
}

function sanitizeDeclineText(text) {
    if (typeof text !== 'string') return null;
    const trimmed = text.trim();
    const isSensitive = RE_SENSITIVE_TEXT.test(trimmed) || RE_SENSITIVE_TEXT.test(trimmed.replace(RE_DIGIT_SEPARATORS, ''));
    return trimmed && !isSensitive ? trimmed.slice(0, MAX_DECLINE_TEXT_LENGTH) : null;
}

// Gateway error callback (CreditGuard errorUrl): the code decides the type, the gateway's own text is kept when safe.
function classifyGatewayDecline({ errorCode, errorText } = {}) {
    const code = typeof errorCode === 'string' && RE_GATEWAY_CODE.test(errorCode.trim()) ? errorCode.trim() : null;
    const type = code && CREDITGUARD_EXPIRED_CARD_CODES.includes(code)
        ? PAYMENT_DECLINE_TYPE.CARD_EXPIRED
        : PAYMENT_DECLINE_TYPE.CARD_DECLINED;
    return {
        error_type: type,
        error_text: sanitizeDeclineText(errorText) || (code ? `gateway_error_${code}` : type),
    };
}

// A payment that failed after the gateway callback (poll status ERROR); error_text is the stable server code.
function classifyPaymentError(errorStatusCode) {
    const code = typeof errorStatusCode === 'string' && errorStatusCode.trim() ? errorStatusCode.trim() : null;
    return {
        error_type: DECLINE_TYPE_BY_PAYMENT_ERROR_CODE[code] || PAYMENT_DECLINE_TYPE.PAYMENT_FAILED,
        error_text: code || PAYMENT_CREDIT_CARD_ERROR_CODES.UNSPECIFIED_ERROR,
    };
}

module.exports = {
    PAYMENT_INFO_TYPE,
    PAYMENT_DECLINE_TYPE,
    PAYMENT_DECLINED_MESSAGE_TYPE,
    CARD_BRAND,
    normalizeCardBrand,
    getPaymentInfoType,
    isPaymentMethodAcceptedStatus,
    sanitizeDeclineText,
    classifyGatewayDecline,
    classifyPaymentError,
};
